import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Check, MessageSquare, FileText, LayoutList, Send, ExternalLink, Paperclip,
  DollarSign, Wallet as WalletIcon, CalendarClock,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { getServiceIcon, getAccent } from "@/lib/serviceIcons";
import WorkspaceModules from "@/components/app/WorkspaceModules";
import { toast } from "sonner";

function StageTracker({ stages, idx }) {
  return (
    <div className="flex items-center">
      {stages.map((st, i) => {
        const complete = i < idx; const current = i === idx;
        return (
          <React.Fragment key={st}>
            <div className="flex flex-col items-center flex-shrink-0">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold border-2 ${complete ? "bg-green-500/20 border-green-500 text-green-400" : current ? "bg-primary-600 border-primary-400 text-white shadow-[0_0_15px_rgba(59,130,246,0.5)]" : "bg-white/5 border-white/10 text-zinc-500"}`}>{complete ? <Check size={15} /> : i + 1}</div>
              <span className={`text-[10px] mt-2 font-medium text-center max-w-[70px] ${current ? "text-white" : "text-zinc-500"}`}>{st}</span>
            </div>
            {i < stages.length - 1 && <div className={`flex-1 h-0.5 mx-1 mb-5 ${i < idx ? "bg-green-500/50" : "bg-white/10"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function WorkspaceDetail({ workflowId }) {
  const navigate = useNavigate();
  const { profile, wallet, refreshWallet } = useApp();
  const [ws, setWs] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [tab, setTab] = useState("overview");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [paying, setPaying] = useState(false);
  const scrollRef = useRef(null);

  const load = () => api.getWorkspace(workflowId).then(setWs).catch(() => navigate("/app/workspaces"));
  useEffect(() => { load(); }, [workflowId]);

  useEffect(() => {
    if (tab === "financials" && !ws.proposal && !invoice) api.getInvoice(workflowId).then(setInvoice).catch(() => {});
    if (tab === "messages") scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [ws, tab]);

  if (!ws) return <div className="h-full flex items-center justify-center text-zinc-500">Loading…</div>;

  const { workflow: w, request, messages, deliverables, agent } = ws;
  const Icon = getServiceIcon(w.icon); const accent = getAccent(w.accent);
  const done = w.current_stage_index >= w.stages.length - 1;

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    try {
      const msg = await api.postWorkspaceMessage(workflowId, "client", text);
      setWs((s) => ({ ...s, messages: [...s.messages, msg] }));
    } finally { setSending(false); }
  };

  const invoiceHtml = (inv) => {
    const rows = inv.items.map((it) => `<tr><td style="padding:8px 0;border-bottom:1px solid #eee">${it.description}</td><td style="padding:8px 0;border-bottom:1px solid #eee;text-align:right">$${it.amount.toLocaleString()}</td></tr>`).join("");
    return `<html><head><title>Invoice - ${w.title}</title></head><body style="font-family:Inter,Arial,sans-serif;max-width:640px;margin:40px auto;color:#111">
      <h1 style="margin:0">DONDR\u00c1E</h1>
      <p style="color:#666;margin:4px 0 24px">Invoice · ${w.title}</p>
      <p><strong>Billed to:</strong> ${ws.client.contact_name}${ws.client.business_name ? ` (${ws.client.business_name})` : ""}</p>
      <table style="width:100%;border-collapse:collapse;margin-top:16px"><tbody>${rows}</tbody></table>
      <div style="margin-top:16px;text-align:right">
        <p style="margin:4px 0">Total: <strong>$${inv.total.toLocaleString()}</strong></p>
        <p style="margin:4px 0;color:#16a34a">Paid: $${inv.paid.toLocaleString()}</p>
        <p style="margin:4px 0;font-size:18px">Balance Due: <strong>$${inv.balance.toLocaleString()}</strong></p>
      </div>
      <p style="color:#999;font-size:12px;margin-top:32px">Thank you for your business. Powered by DONDR\u00c1E.</p>
    </body></html>`;
  };

  const downloadPdf = () => {
    if (!invoice) return;
    const win = window.open("", "_blank");
    if (!win) { toast.error("Please allow pop-ups to download the invoice."); return; }
    win.document.write(invoiceHtml(invoice));
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  const emailInvoice = () => {
    if (!invoice) return;
    const subject = encodeURIComponent(`Invoice — ${w.title}`);
    const body = encodeURIComponent(
      `Invoice for ${w.title}\n\n` +
      invoice.items.map((it) => `• ${it.description}: $${it.amount.toLocaleString()}`).join("\n") +
      `\n\nTotal: $${invoice.total.toLocaleString()}\nPaid: $${invoice.paid.toLocaleString()}\nBalance Due: $${invoice.balance.toLocaleString()}\n\nPowered by DONDRÁE.`
    );
    window.location.href = `mailto:${ws.client.email || ""}?subject=${subject}&body=${body}`;
  };

  const payAmount = async (amount, instId) => {
    if (paying) return;
    setPaying(true);
    try {
      const bal = wallet?.balance ?? 0;
      if (bal < amount) {
        await api.addFunds(profile.id, Math.ceil(amount - bal));
        await refreshWallet();
      }
      const updated = instId ? await api.payInstallment(workflowId, instId) : await api.payInvoice(workflowId, amount);
      await refreshWallet();
      setInvoice(updated);
      toast.success(`Payment of $${amount.toLocaleString()} received. Thank you!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment failed.");
    } finally { setPaying(false); }
  };

  const payProposalBalance = async () => {
    const prop = ws.proposal;
    if (!prop || prop.balance <= 0 || paying) return;
    setPaying(true);
    try {
      const res = await api.payProposal(prop.id, { origin_url: window.location.origin, amount: prop.balance });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start secure checkout.");
      setPaying(false);
    }
  };

  const TABS = [[
    "overview", "Overview", LayoutList], ["eventhub", "Event Hub", CalendarClock], ["messages", `Messages${messages.length ? ` (${messages.length})` : ""}`, MessageSquare], ["financials", "Financials", DollarSign], ["files", `Files${deliverables.length ? ` (${deliverables.length})` : ""}`, FileText]];

  return (
    <div className="h-full overflow-y-auto relative hide-scrollbar">
      <div className="sticky top-0 z-30 glass-nav border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/app/workspaces")} className="text-zinc-400 hover:text-white font-medium text-sm flex items-center transition-colors"><ArrowLeft size={16} className="mr-2" /> All Workspaces</button>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 pb-24 fade-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className={`w-14 h-14 rounded-2xl border flex items-center justify-center ${accent.bg} ${accent.text} ${accent.border}`}><Icon size={26} /></div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white tracking-tight">{w.title}</h1>
            <p className="text-sm text-zinc-500">{done ? "Project completed" : `In progress — ${w.stages[w.current_stage_index]}`}</p>
          </div>
          {w.total ? <div className="text-right"><div className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider">Project Value</div><div className="text-xl font-extrabold text-white">${w.total.toLocaleString()}</div></div> : null}
        </div>

        {/* Assigned team */}
        <div className="glass-panel rounded-2xl p-4 mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-900/40 border border-blue-500/50 overflow-hidden flex items-center justify-center text-blue-300 font-bold flex-shrink-0">{agent.photo ? <img src={agent.photo} alt={agent.name} className="w-full h-full object-cover" /> : agent.name[0]}</div>
          <div className="flex-1"><div className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">Your Project Lead</div><div className="text-white font-bold text-sm">{agent.name}</div><div className="text-xs text-zinc-500">{agent.role}</div></div>
          <button onClick={() => setTab("messages")} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-bold hover:bg-primary-500 transition flex items-center"><MessageSquare size={15} className="mr-1.5" /> Message</button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/10 mb-6 overflow-x-auto hide-scrollbar">
          {TABS.map(([k, l, TIcon]) => (
            <button key={k} onClick={() => setTab(k)} className={`flex-shrink-0 whitespace-nowrap px-4 sm:px-5 py-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${tab === k ? "border-primary-500 text-white" : "border-transparent text-zinc-500 hover:text-white"}`}><TIcon size={15} /> {l}</button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="space-y-6">
            <div className="glass-panel rounded-2xl p-6">
              <h3 className="font-bold text-white mb-6">Project Progress</h3>
              <StageTracker stages={w.stages} idx={w.current_stage_index} />
            </div>
            {w.package && (
              <div className="glass-panel rounded-2xl p-6">
                <h3 className="font-bold text-white mb-4">Package &amp; Add-ons</h3>
                <div className="flex justify-between text-sm mb-2"><span className="text-zinc-300">{w.package.name}</span><span className="text-white font-bold">${w.package.price?.toLocaleString()}</span></div>
                {(w.addons || []).map((a) => (<div key={a.id} className="flex justify-between text-sm mb-2 text-zinc-400"><span>+ {a.name}</span><span>${a.price}</span></div>))}
                <div className="border-t border-white/10 mt-3 pt-3 flex justify-between"><span className="text-zinc-400 font-bold text-xs uppercase tracking-wider">Total</span><span className="text-white font-extrabold">${w.total?.toLocaleString()}</span></div>
              </div>
            )}
            {request && request.form_data && (
              <div className="glass-panel rounded-2xl p-6">
                <h3 className="font-bold text-white mb-4">Project Brief</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                  {Object.entries(request.form_data).map(([k, v]) => v ? (
                    <div key={k}><div className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider">{k.replace(/_/g, " ")}</div><div className="text-sm text-zinc-200">{String(v)}</div></div>
                  ) : null)}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "eventhub" && (
          <WorkspaceModules workflowId={workflowId} workflow={w} by="client" onChange={(updated) => setWs((s) => ({ ...s, workflow: { ...s.workflow, ...updated } }))} />
        )}

        {tab === "messages" && (
          <div className="glass-panel rounded-2xl overflow-hidden flex flex-col" style={{ height: "500px" }}>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 hide-scrollbar bg-black/20">
              {messages.length === 0 && <p className="text-center text-zinc-500 text-sm mt-10">No messages yet. Say hello to your project lead {agent.name.split(" ")[0]}.</p>}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
                  <div className="max-w-[78%]">
                    <div className={`text-[10px] mb-1 ${m.sender === "client" ? "text-right text-zinc-500" : "text-zinc-500"}`}>{m.sender_name}</div>
                    <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.sender === "client" ? "bg-primary-600 text-white rounded-br-sm" : "bg-white/5 border border-white/10 text-zinc-200 rounded-bl-sm"}`}>{m.text}</div>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={send} className="p-4 border-t border-white/10 flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={`Message ${agent.name.split(" ")[0]}…`} className="flex-1 px-4 py-3 rounded-xl dark-input text-sm" />
              <button type="submit" disabled={sending || !input.trim()} className="w-12 h-12 flex items-center justify-center bg-primary-600 text-white rounded-xl hover:bg-primary-500 transition disabled:opacity-40"><Send size={18} /></button>
            </form>
          </div>
        )}

        {tab === "financials" && ws.proposal && (
          <div className="space-y-6" data-testid="ws-financials-proposal">
            {(() => {
              const prop = ws.proposal;
              const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
              return (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="glass-panel rounded-2xl p-5"><div className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider mb-1">Invoice Total</div><div className="text-2xl font-extrabold text-white" data-testid="ws-fin-total">{money(prop.total)}</div></div>
                    <div className="glass-panel rounded-2xl p-5"><div className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider mb-1">Paid</div><div className="text-2xl font-extrabold text-green-400" data-testid="ws-fin-paid">{money(prop.amount_paid)}</div></div>
                    <div className="glass-panel rounded-2xl p-5"><div className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider mb-1">Balance Due</div><div className={`text-2xl font-extrabold ${prop.balance > 0 ? "text-orange-400" : "text-white"}`} data-testid="ws-fin-balance">{money(prop.balance)}</div></div>
                  </div>

                  <div className="glass-panel rounded-2xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/10 bg-white/5 flex items-center justify-between">
                      <h3 className="font-bold text-white text-sm">Invoice — {prop.title || w.title}</h3>
                      <a href={api.proposalPdfUrl(prop.id)} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition flex items-center"><FileText size={13} className="mr-1.5" /> Download PDF</a>
                    </div>
                    <table className="w-full text-sm">
                      <tbody className="divide-y divide-white/5">
                        {(prop.line_items || []).map((it, i) => {
                          const net = (Number(it.amount) || 0) - (Number(it.discount) || 0);
                          return (
                            <tr key={i} data-testid="ws-fin-line">
                              <td className="px-6 py-3 text-zinc-200">{it.description}{it.discount ? <span className="text-[10px] font-bold text-teal-400 uppercase ml-2">-{money(it.discount)} disc</span> : null}</td>
                              <td className="px-6 py-3 text-right font-bold w-28 text-white">{money(net)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {prop.balance > 0 ? (
                    <div className="glass-panel rounded-2xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 border border-green-500/20">
                      <div>
                        <div className="text-white font-bold">Pay your balance</div>
                        <div className="text-xs text-zinc-400 mt-1">Secure payment powered by Stripe — you'll be redirected to a secure checkout page.</div>
                      </div>
                      <button onClick={payProposalBalance} disabled={paying} className="px-6 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-500 transition disabled:opacity-50 whitespace-nowrap" data-testid="ws-fin-pay-btn">{paying ? "Redirecting…" : `Pay ${money(prop.balance)} Now`}</button>
                    </div>
                  ) : (
                    <div className="glass-panel rounded-2xl p-6 text-center border border-green-500/20" data-testid="ws-fin-paid-banner">
                      <Check size={28} className="text-green-400 mx-auto mb-2" />
                      <div className="text-white font-bold">Paid in full — thank you!</div>
                    </div>
                  )}

                  <div className="glass-panel rounded-2xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/10 bg-white/5"><h3 className="font-bold text-white text-sm">Payment History</h3></div>
                    <div className="divide-y divide-white/5">
                      {(prop.payments || []).length === 0 && <div className="px-6 py-6 text-center text-zinc-500 text-sm">No payments yet.</div>}
                      {(prop.payments || []).map((p, i) => (
                        <div key={i} className="px-6 py-3 flex justify-between items-center text-sm" data-testid="ws-fin-history-row">
                          <span className="text-zinc-300">{p.at ? new Date(p.at).toLocaleDateString() : ""} · {(p.method || "manual").replace("_", " ")}{p.note ? ` · ${p.note}` : ""}</span>
                          <span className="text-green-400 font-bold">+{money(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {tab === "financials" && !ws.proposal && (
          <div className="space-y-6">
            {!invoice ? (
              <div className="glass-panel rounded-2xl p-12 text-center text-zinc-500">Loading invoice…</div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div className="glass-panel rounded-2xl p-5"><div className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider mb-1">Invoice Total</div><div className="text-2xl font-extrabold text-white">${invoice.total.toLocaleString()}</div></div>
                  <div className="glass-panel rounded-2xl p-5"><div className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider mb-1">Paid</div><div className="text-2xl font-extrabold text-green-400">${invoice.paid.toLocaleString()}</div></div>
                  <div className="glass-panel rounded-2xl p-5"><div className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider mb-1">Balance Due</div><div className={`text-2xl font-extrabold ${invoice.balance > 0 ? "text-orange-400" : "text-white"}`}>${invoice.balance.toLocaleString()}</div></div>
                </div>

                <div className="glass-panel rounded-2xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-white/10 bg-white/5 flex items-center justify-between">
                    <h3 className="font-bold text-white text-sm">Invoice — {w.title}</h3>
                    <div className="flex gap-2">
                      <button onClick={downloadPdf} className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition flex items-center"><FileText size={13} className="mr-1.5" /> Download PDF</button>
                      <button onClick={emailInvoice} className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition flex items-center"><Send size={13} className="mr-1.5" /> Email</button>
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-white/5">
                      {invoice.items.map((it) => (
                        <tr key={it.id}>
                          <td className="px-6 py-3 text-zinc-200">{it.description}</td>
                          <td className="px-6 py-3 text-right">{it.amount < 0 ? <span className="text-[10px] font-bold text-teal-400 uppercase mr-3">Credit</span> : it.paid ? <span className="text-[10px] font-bold text-green-400 uppercase mr-3">Paid</span> : <span className="text-[10px] font-bold text-orange-400 uppercase mr-3">Due</span>}</td>
                          <td className={`px-6 py-3 text-right font-bold w-28 ${it.amount < 0 ? "text-teal-400" : "text-white"}`}>{it.amount < 0 ? `-$${Math.abs(it.amount).toLocaleString()}` : `$${it.amount.toLocaleString()}`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {invoice.plan?.enabled && invoice.plan.installments.length > 0 && (
                  <div className="glass-panel rounded-2xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/10 bg-white/5"><h3 className="font-bold text-white text-sm">Payment Plan</h3></div>
                    <div className="divide-y divide-white/5">
                      {invoice.plan.installments.map((inst) => (
                        <div key={inst.id} className="px-6 py-4 flex items-center justify-between">
                          <div><div className="text-white font-medium text-sm">{inst.label}</div><div className="text-xs text-zinc-500">{inst.status === "paid" ? "Paid" : "Due"}</div></div>
                          <div className="flex items-center gap-4">
                            <span className="text-white font-bold">${inst.amount.toLocaleString()}</span>
                            {inst.status === "paid" ? <span className="text-green-400 flex items-center text-sm font-bold"><Check size={15} className="mr-1" /> Paid</span> : <button onClick={() => payAmount(inst.amount, inst.id)} disabled={paying} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-bold hover:bg-primary-500 transition disabled:opacity-50">Pay</button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {invoice.balance > 0 && (
                  <div className="glass-panel rounded-2xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="text-white font-bold">Pay your balance</div>
                      <div className="text-xs text-zinc-400 flex items-center mt-1"><WalletIcon size={13} className="mr-1.5 text-green-400" /> Wallet balance: ${(wallet?.balance ?? 0).toLocaleString()} · shortfall auto-added if needed</div>
                    </div>
                    <button onClick={() => payAmount(invoice.balance)} disabled={paying} className="px-6 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-500 transition disabled:opacity-50">{paying ? "Processing…" : `Pay $${invoice.balance.toLocaleString()} Now`}</button>
                  </div>
                )}

                <div className="glass-panel rounded-2xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-white/10 bg-white/5"><h3 className="font-bold text-white text-sm">Payment History</h3></div>
                  <div className="divide-y divide-white/5">
                    {invoice.payments.length === 0 && <div className="px-6 py-6 text-center text-zinc-500 text-sm">No payments yet.</div>}
                    {invoice.payments.map((p) => (
                      <div key={p.id} className="px-6 py-3 flex justify-between items-center text-sm">
                        <span className="text-zinc-300">{new Date(p.date).toLocaleDateString()} · {p.method}</span>
                        <span className="text-green-400 font-bold">+${p.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-zinc-600 text-center">Demo mode — payments draw from your DONDRÁE Wallet; no real charges are made.</p>
              </>
            )}
          </div>
        )}

        {tab === "files" && (
          <div className="space-y-3">
            {deliverables.length === 0 && (
              <div className="glass-panel rounded-2xl p-12 text-center"><Paperclip size={28} className="text-zinc-600 mx-auto mb-3" /><p className="text-zinc-400 text-sm">No files yet. Deliverables shared by your team will appear here.</p></div>
            )}
            {deliverables.map((d) => (
              <div key={d.id} className="glass-panel rounded-2xl p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-primary-900/40 border border-primary-500/30 text-primary-400 flex items-center justify-center flex-shrink-0"><FileText size={20} /></div>
                <div className="flex-1 min-w-0"><div className="text-white font-bold text-sm">{d.title}</div>{d.note && <div className="text-xs text-zinc-500">{d.note}</div>}</div>
                {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm font-bold hover:bg-white/20 transition flex items-center">Open <ExternalLink size={13} className="ml-1.5" /></a>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
